import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, Loader2, MessageSquareReply } from "lucide-react";
import { toast } from "sonner";

export default function Reviews() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("cms:manage");
  const [rows, setRows] = useState(null);
  const [respond, setRespond] = useState(null);
  const [text, setText] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/reviews"); setRows(data); }
    catch (e) { toast.error(apiError(e)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, fields) => { try { await api.put(`/reviews/${id}`, fields); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveResponse = async () => { await patch(respond.id, { response: text, status: "RESPONDED" }); setRespond(null); setText(""); };

  const reviews = rows || [];
  const avg = reviews.length ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length).toFixed(1) : "0.0";
  const kpis = [["Average Rating", avg], ["Total Reviews", reviews.length], ["New", reviews.filter((r) => r.status === "NEW").length], ["Pending Responses", reviews.filter((r) => r.status === "NEW").length]];

  return (
    <div data-testid="reviews-page">
      <PageHeader title="Reviews & Reputation" subtitle="Monitor and respond to reviews; publish testimonials to the website."
        actions={<span className="inline-flex items-center gap-1.5 rounded-full bg-ochre/15 text-ochre text-xs font-bold px-3 py-1.5">DEMO AUTOMATION</span>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {kpis.map(([l, v]) => (
          <div key={l} className="border border-border rounded-lg bg-card p-4" data-testid={`review-kpi-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p>
            <p className="font-heading text-2xl font-bold mt-1 flex items-center gap-1">{v}{l === "Average Rating" && <Star className="w-5 h-5 fill-gold text-gold" />}</p>
          </div>
        ))}
      </div>

      {rows === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid md:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <div key={r.id} className="border border-border rounded-lg bg-card p-5" data-testid="review-card">
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-gold text-gold" : "text-muted"}`} />)}</div>
                <StatusBadge value={r.status} />
              </div>
              <p className="text-[15px] mt-3 leading-relaxed">"{r.text}"</p>
              <p className="text-sm text-muted-foreground mt-2">{r.name} · {r.source} · {fmtDate(r.created_at)}</p>
              {r.response && <div className="mt-3 rounded-lg bg-secondary p-3 text-sm"><span className="font-semibold">Response: </span>{r.response}</div>}
              {canManage && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => { setRespond(r); setText(r.response || ""); }} data-testid="review-respond"><MessageSquareReply className="w-4 h-4 mr-1.5" /> Respond</Button>
                  <Button size="sm" variant="outline" onClick={() => patch(r.id, { status: "PUBLISHED" })} data-testid="review-publish">Publish</Button>
                  <Button size="sm" variant="ghost" onClick={() => patch(r.id, { status: "HIDDEN" })} data-testid="review-hide">Hide</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!respond} onOpenChange={(o) => !o && setRespond(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respond to {respond?.name}</DialogTitle></DialogHeader>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Write a response…" data-testid="review-response-text" />
          <DialogFooter><Button variant="outline" onClick={() => setRespond(null)}>Cancel</Button><Button onClick={saveResponse} data-testid="review-response-save">Save Response</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
